import {
  Icons,
  message,
  Modal,
  Upload,
  UploadFile,
  UploadProps,
} from '@pankod/refine-antd'
import React from 'react'
import { useInvalidate } from '@pankod/refine-core'

import { getGuruToken } from 'src/helpers/session'

const UPLOAD_FILE_SIZE_LIMIT = 16 * 1024 * 1024 // 16 MB

type UploadSupportingDocumentModalProps = {
  visible: boolean
  onClose: () => void
  programId: string
  certificateId: string
}

const UploadSupportingDocumentModal = ({
  visible,
  onClose,
  programId,
  certificateId,
}: UploadSupportingDocumentModalProps) => {
  const [fileList, setFileList] = React.useState<UploadFile[]>([])
  const [pendingFile, setPendingFile] = React.useState<File | null>(null)
  const [isSaving, setIsSaving] = React.useState(false)
  const invalidate = useInvalidate()

  const handleClose = () => {
    setFileList([])
    setPendingFile(null)
    onClose()
  }

  const handleBeforeUpload: UploadProps['beforeUpload'] = (file) => {
    if (file.type !== 'application/pdf') {
      message.error('Anda hanya dapat mengunggah file PDF')
      return Upload.LIST_IGNORE
    }
    if (file.size > UPLOAD_FILE_SIZE_LIMIT) {
      message.error('Ukuran file tidak boleh lebih dari 16 MB')
      return Upload.LIST_IGNORE
    }
    setPendingFile(file)
    return false
  }

  const handleChange: UploadProps['onChange'] = ({ fileList }) => {
    setFileList(fileList)
  }

  const handleSave = async () => {
    if (!pendingFile) return

    setIsSaving(true)
    try {
      const formData = new FormData()
      formData.append('file', pendingFile)
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_LMS_API_URL}/programs/${programId}/certificate-templates/${certificateId}/supporting-document`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${getGuruToken()}` },
          body: formData,
        },
      )
      if (!response.ok) throw new Error('Upload gagal')

      message.success('Dokumen pendukung berhasil diunggah')
      invalidate({
        invalidates: ['list'],
        dataProviderName: 'lms',
        resource: `programs/${programId}/certificate-templates`,
      })
      handleClose()
    } catch {
      message.error('Gagal mengunggah dokumen, silakan coba lagi')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      title="Unggah Dokumen Pendukung"
      visible={visible}
      onCancel={handleClose}
      onOk={handleSave}
      okText="Simpan"
      cancelText="Batal"
      okButtonProps={{ disabled: !pendingFile, loading: isSaving }}
      cancelButtonProps={{ disabled: isSaving }}
      destroyOnClose
    >
      <Upload.Dragger
        accept=".pdf"
        beforeUpload={handleBeforeUpload}
        onChange={handleChange}
        onRemove={() => {
          setPendingFile(null)
          return true
        }}
        fileList={fileList}
        maxCount={1}
      >
        <p className="ant-upload-drag-icon">
          <Icons.InboxOutlined />
        </p>
        <p className="ant-upload-text">
          Klik atau seret berkas ke area ini untuk mengunggah
        </p>
        <p className="ant-upload-hint">Hanya file PDF, maksimal 16 MB</p>
      </Upload.Dragger>
    </Modal>
  )
}

export default UploadSupportingDocumentModal
